import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import type { AmbientScribeTemplateType } from "services/types/ambient-scribe-template.type";

/**
 * Which surface opened the Insert Scribe dialog, and what it wants generated (SN-19067).
 *
 * Unset means the default chart-note behaviour. The audiology report summary sets
 * `templateType: "AudiologyReport"` + `parentId: <report id>`, which switches the dialog's
 * Scribe tab over to the generated-notes flow.
 */
type InsertScribeRequest = {
  chartNoteId?: number;
  show: boolean;
  templateType?: AmbientScribeTemplateType;
  /** The artifact the generated note belongs to — an audiology report id for `AudiologyReport`. */
  parentId?: number;
};

interface PatientChartNotes {
  selectedPrintNotesIds: number[];
  selectedNoteIds: number[];
  showPrintCheckboxes: boolean;
  pinnedChartNotesFormCollapsed: boolean;
  showInsertScribeForChartNoteId: InsertScribeRequest;
  activeTab: string;
  activeTabPatientId: string | null;
}

// Define the initial state using that type
const initialState: PatientChartNotes = {
  selectedPrintNotesIds: [],
  selectedNoteIds: [],
  showPrintCheckboxes: false,
  pinnedChartNotesFormCollapsed: true,
  showInsertScribeForChartNoteId: {
    chartNoteId: undefined,
    show: false
  },
  activeTab: "all",
  activeTabPatientId: null
};

export const patientChartNotesSlice = createSlice({
  name: "patientChartNotes",
  initialState,
  reducers: {
    setSelectedNoteIds: (state, action: PayloadAction<number[]>) => {
      state.selectedNoteIds = action.payload;
    },
    clearSelectedNoteIds: (state) => {
      state.selectedNoteIds = [];
    },
    addSelectedNoteId: (state, action: PayloadAction<number>) => {
      const selectedNoteIdsSet = new Set(state.selectedNoteIds);
      selectedNoteIdsSet.add(action.payload);
      state.selectedNoteIds = Array.from(selectedNoteIdsSet);
    },
    deleteSelectedNoteId: (state, action: PayloadAction<number>) => {
      const selectedNoteIdsSet = new Set(state.selectedNoteIds);
      selectedNoteIdsSet.delete(action.payload);
      state.selectedNoteIds = Array.from(selectedNoteIdsSet);
    },
    collapsePinnedChartNotesForm: (state) => {
      state.pinnedChartNotesFormCollapsed = true;
    },
    expandPinnedChartNotesForm: (state) => {
      state.pinnedChartNotesFormCollapsed = false;
    },
    addSelectedPrintNoteId: (
      state,
      action: PayloadAction<{ id: number; selected: boolean }>
    ) => {
      const { id, selected } = action.payload;

      const idsSet = new Set(state.selectedPrintNotesIds);
      idsSet.delete(id);

      if (selected && !idsSet.has(id)) {
        idsSet.add(id);
      }

      state.selectedPrintNotesIds = Array.from(idsSet);
    },
    bulkSelectedPrintNoteId: (
      state,
      action: PayloadAction<{ ids: number[] }>
    ) => {
      const { ids } = action.payload;
      state.selectedPrintNotesIds = [...ids];
    },
    toggleShowPrintCheckboxes: (state, action: PayloadAction<boolean>) => {
      state.showPrintCheckboxes = action.payload;
    },
    showInsertScribe: (state, action: PayloadAction<InsertScribeRequest>) => {
      const { chartNoteId, show, templateType, parentId } = action.payload;
      state.showInsertScribeForChartNoteId = {
        chartNoteId: chartNoteId
          ? chartNoteId
          : state.showInsertScribeForChartNoteId.chartNoteId,
        show,
        // Deliberately NOT sticky, unlike `chartNoteId` above. These say what THIS open is
        // for, so carrying them forward would leave a chart-note-opened dialog stuck in
        // audiology mode after one audiology open. Closing (`{ show: false }`) clears them.
        templateType,
        parentId
      };
    },
    setActiveTab: (
      state,
      action: PayloadAction<{ tab: string; patientId: string }>
    ) => {
      state.activeTab = action.payload.tab;
      state.activeTabPatientId = action.payload.patientId;
    }
  }
});

// Action creators are generated for each case reducer function
export const {
  setSelectedNoteIds,
  clearSelectedNoteIds,
  addSelectedNoteId,
  deleteSelectedNoteId,
  collapsePinnedChartNotesForm,
  expandPinnedChartNotesForm,
  addSelectedPrintNoteId,
  bulkSelectedPrintNoteId,
  toggleShowPrintCheckboxes,
  showInsertScribe,
  setActiveTab
} = patientChartNotesSlice.actions;

const patientChartNotesSelector = (state: RootState) => state.patientChartNotes;

export const selectSelectedNoteIds = createSelector(
  patientChartNotesSelector,
  (patientChartNotes) => patientChartNotes.selectedNoteIds
);

export const selectSelectedNoteIdsSet = createSelector(
  selectSelectedNoteIds,
  (selectedNoteIds) => new Set(selectedNoteIds)
);

export const pinnedChartNotesFormCollapsedSelector = (state: RootState) =>
  state.patientChartNotes.pinnedChartNotesFormCollapsed;

export const selectSelectedPrintNotesIds = createSelector(
  patientChartNotesSelector,
  (patientChartNotes) => patientChartNotes.selectedPrintNotesIds
);

export const selectSelectedPrintNotesIdsSet = createSelector(
  selectSelectedPrintNotesIds,
  (selectedPrintNotesIds) => new Set(selectedPrintNotesIds)
);

export const selectShowPrintCheckboxes = (state: RootState) =>
  state.patientChartNotes.showPrintCheckboxes;

export const showInsertScribeForChartNoteId = (state: RootState) =>
  state.patientChartNotes.showInsertScribeForChartNoteId;

export const selectActiveTab = createSelector(
  patientChartNotesSelector,
  (s) => s.activeTab
);

export const selectActiveTabPatientId = createSelector(
  patientChartNotesSelector,
  (s) => s.activeTabPatientId
);

export default patientChartNotesSlice.reducer;
